import { api } from "../api/client";
import { Step } from "../types/sop";

/**
 * Get presigned URLs for multiple image references
 */
export const getImageUrls = async (imageIds: string[]): Promise<Record<string, string>> => {
  if (!imageIds.length) return {};

  try {
    const response = await api.post("/download_urls", {
      image_ids: imageIds
    });
    return response.data.urls;
  } catch (error) {
    console.error("Error getting image URLs:", error);
    throw new Error("Failed to get image URLs");
  }
};

// interface ImageUrlState {
//   url: string;
//   expiresAt: number;
// }

/**
 * Extract image IDs from HTML (e.g. step details) as normalized UUIDs, in order of appearance.
 * Handles data-image-id="uuid", data-image-id="image://uuid", and image://uuid in text.
 */
export const extractImageIdsFromHtml = (html: string): string[] => {
  if (!html?.trim()) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  const uuidRe =
    /(?:data-image-id="(?:image:\/\/)?([0-9a-f-]{36})"|image:\/\/([0-9a-f-]{36}))/gi;
  let m;
  while ((m = uuidRe.exec(html)) !== null) {
    const id = (m[1] ?? m[2]).toLowerCase();
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
};

/**
 * Get a short text context for where an image appears in HTML (e.g. "after '…Put on personal protective…'").
 * Finds the first img with this imageId and returns the preceding text, stripped and truncated.
 */
export const getImageContextInHtml = (
  html: string,
  imageId: string,
  maxChars = 50,
): string => {
  if (!html?.trim() || !imageId) return "";
  const id = imageId.toLowerCase();
  const lowerHtml = html.toLowerCase();
  const idIdx = lowerHtml.indexOf(id);
  if (idIdx === -1) return "";
  // Walk back to find the start of the <img> tag containing this id
  const tagStart = lowerHtml.lastIndexOf("<img", idIdx);
  const cutoff = tagStart !== -1 ? tagStart : idIdx;
  const before = html.slice(0, cutoff);
  const stripped = before
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!stripped) return "";
  return stripped.length <= maxChars
    ? stripped
    : "…" + stripped.slice(-maxChars);
};

/**
 * Extract image references from HTML content
 */
export const extractImageReferences = (content: string): string[] => {
  const refs = new Set<string>();

  // Extract image:// references
  const imageRefRegex = /image:\/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const matches = content.match(imageRefRegex) || [];
  matches.forEach(ref => refs.add(ref));

  // Extract data-image-id attributes (plain uuid and image://uuid)
  const imgTagRegex =
    /<img[^>]*data-image-id="(?:image:\/\/)?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"[^>]*>/gi;
  let match;
  while ((match = imgTagRegex.exec(content)) !== null) {
    refs.add(`image://${match[1]}`);
  }

  return Array.from(refs);
};

/**
 * Replace image references in HTML content with presigned URLs
 */
export const replaceImageReferences = async (
  content: string,
  getCurrentUrl: (id: string) => Promise<string | null>
): Promise<string> => {
  let result = content;
  const refs = extractImageReferences(content);
  // TODO use imageUrls to reduce redundant calls to getCurrentUrl
  // console.log(imageUrls);

  // First, ensure all URLs are fetched
  const urlPromises = refs.map(async (ref) => {
    const id = ref.replace('image://', '');
    return getCurrentUrl(id);
  });

  const urls = await Promise.all(urlPromises);
  const urlMap = new Map(refs.map((ref, i) => [ref, urls[i]]));

  for (const ref of refs) {
    const currentUrl = urlMap.get(ref);
    if (currentUrl) {
      const uuid = ref.replace('image://', '');

      // Handle both UUID-only and full image:// prefixed data-image-id attributes
      const imgRegex1 = new RegExp(`<img[^>]*data-image-id="${uuid}"[^>]*>`, 'g');
      const imgRegex2 = new RegExp(`<img[^>]*data-image-id="image://${uuid}"[^>]*>`, 'g');

      const preserveSrc = (match: string) =>
        match.includes('data-annotated-data=') ||
        match.includes('data-cropped-data=');

      result = result.replace(imgRegex1, (match) => {
        // Preserve user-generated src (annotations, crops) — these store the
        // pixel data inline (typically as data: URLs) and must not be replaced
        // with the underlying signed S3 URL on re-render.
        if (preserveSrc(match)) {
          return match;
        }
        return match.replace(/src="[^"]*"/, `src="${currentUrl}"`);
      });

      result = result.replace(imgRegex2, (match) => {
        if (preserveSrc(match)) {
          return match;
        }
        return match.replace(/src="[^"]*"/, `src="${currentUrl}"`);
      });
    }
  }

  return result;
};

/**
 * Get all image references from a list of steps
 */
export const getImageReferencesFromSteps = (steps: Step[]): string[] => {
  const imageIds = new Set<string>();

  // Collect all image references from steps
  steps.forEach(step => {
    // Get image references from step details
    const detailsRefs = extractImageReferences(step.details);
    detailsRefs.forEach(id => imageIds.add(id));

    // Get image references from step's imageReferences array
    step.imageReferences.forEach(id => imageIds.add(id));
  });

  return Array.from(imageIds);
};